import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  forwardRef,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

/**
 * Port of legacy `addEdit.html` `uib-typeahead` filter strip (L507–547):
 * text input + type-to-filter list, `typeahead-min-length="0"`, chevron
 * clears and refocuses to reopen (`ctrl.open`), optional loading spinner,
 * `limitTo`-style cap, and dropdown styling per `[uib-typeahead-popup]`
 * rules in the same template (max-height 300px, orange active row).
 */
@Component({
    selector: 'gtw-efile-typeahead-select',
    templateUrl: './efile-typeahead-select.component.html',
    styleUrls: ['./efile-typeahead-select.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => EfileTypeaheadSelectComponent),
            multi: true,
        },
    ],
    standalone: false
})
export class EfileTypeaheadSelectComponent implements ControlValueAccessor, OnChanges, OnDestroy {
  @ViewChild('inputEl', { static: false }) inputRef?: ElementRef<HTMLInputElement>;

  /** Option rows (e.g. allForms / form_subs / parent_elems). */
  @Input() options: any[] = [];

  /** Property to read for label/value (e.g. FORM, FORM_SUB, PARENT_ELEMENT_NAME). */
  @Input() displayKey = '';

  @Input() placeholder = '';

  @Input() disabled = false;

  /** When true, chevron is hidden and a spinner is shown (legacy dd-loader). */
  @Input() loading = false;

  /** Mirrors `| limitTo: 30` on subs/parent; 0 = no cap. */
  @Input() maxResults = 30;

  /** Fixed panel coordinates (viewport) when true — avoids clipping in overflow:hidden parents. */
  @Input() appendToBody = true;

  /**
   * Fires ONLY when the user commits a value via the dropdown list
   * (mouse click on a row or Enter on a keyboard-highlighted row) —
   * direct port of legacy `uib-typeahead`'s
   * `typeahead-on-select="..."` semantics (`addEdit.html` L509:
   * `typeahead-on-select="ctrl.clickIcon('add',$item)"`).
   *
   * Does NOT fire on:
   *   - free-text typing (`onInputChange`) — `ngModelChange` already
   *     covers that; cascading filter loads on every keystroke caused
   *     dropdown 2 + 3 to re-fetch from the backend per character.
   *   - chevron clear (`clearAndOpen`) — legacy clears
   *     `selectedForm = ''` directly without invoking
   *     `typeahead-on-select`, so dependent dropdowns retain their
   *     prior options (matches `addEdit.html` L511 chevron handler).
   *
   * Hosts that need cascade-on-select behaviour bind
   * `(selectionChange)` for the cascade and keep `(ngModelChange)`
   * for plain model sync.
   */
  @Output() selectionChange = new EventEmitter<string | null>();

  panelOpen = false;
  displayText = '';
  activeIndex = -1;

  filtered: any[] = [];

  panelTop = 0;
  panelLeft = 0;
  panelWidth = 0;

  private value: string | null = '';
  private onChange: (v: string | null) => void = () => {};
  private onTouched: () => void = () => {};

  private scrollParents: HTMLElement[] = [];

  constructor(private cdr: ChangeDetectorRef, private hostRef: ElementRef<HTMLElement>) {}

  ngOnChanges(ch: SimpleChanges): void {
    if (ch['options'] && this.panelOpen) {
      this.rebuildFiltered();
      this.activeIndex =
        this.filtered.length > 0
          ? Math.min(Math.max(this.activeIndex, 0), this.filtered.length - 1)
          : -1;
      this.cdr.markForCheck();
    }
  }

  ngOnDestroy(): void {
    this.detachScrollListeners();
  }

  writeValue(v: string | null): void {
    this.value = v ?? '';
    this.displayText = this.value;
    this.cdr.markForCheck();
  }

  registerOnChange(fn: (v: string | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
    this.cdr.markForCheck();
  }

  trackByIndex(i: number): number {
    return i;
  }

  getItemLabel(item: any): string {
    if (!item || !this.displayKey) return '';
    const raw = item[this.displayKey];
    return raw == null ? '' : String(raw);
  }

  onInputChange(text: string): void {
    this.displayText = text;
    // Legacy parity (`addEdit.html` L515/L536/L545): `uib-typeahead` is bound
    // to `ng-model` so every keystroke commits to the model immediately.
    // Without this, free-typed Form Sub / Parent Element values were lost
    // on blur because `syncDisplayToValue()` reverted `displayText` back
    // to whatever was last selected. Commit on every input event so the
    // parent component's two-way binding mirrors AngularJS behaviour.
    this.value = text;
    this.onChange(this.value);
    this.openPanel();
    this.cdr.markForCheck();
  }

  onFocus(): void {
    // Focus alone must NOT open the panel — only the chevron click opens
    // the dropdown (port adjusted per UX requirement: "click should work
    // only on the arrow click. no other place should trigger dropdown to
    // open"). Typing still opens the panel via `onInputChange` so the
    // standard type-to-filter behaviour stays intact. This also covers
    // the case where `clearAndOpen` focuses the input — `clearAndOpen`
    // calls `openPanel()` explicitly, so the panel still opens on
    // chevron click.
    if (this.disabled) return;
    // intentionally NOT calling openPanel() here
  }

  onBlur(): void {
    this.onTouched();
    setTimeout(() => {
      if (!this.panelOpen) return;
      this.closePanel();
      // Do NOT call `syncDisplayToValue()` here — legacy `uib-typeahead`
      // keeps the typed text in the model on blur (it is just
      // `ng-model` on a regular input). Reverting the input to the last
      // selected option destroyed user-typed Form Sub / Parent Element
      // values whenever a user typed a new value and clicked outside
      // the dropdown. The typed text is already committed via
      // `onInputChange`, so blur only needs to close the panel.
      this.cdr.markForCheck();
    }, 180);
  }

  selectItem(item: any): void {
    const next = this.getItemLabel(item);
    this.value = next;
    this.displayText = next;
    this.onChange(this.value);
    // Legacy parity with `typeahead-on-select` (addEdit.html L509):
    // dropdown-list selection is the ONLY commit path that triggers
    // cascading filter loads (e.g. Form -> Form Sub + Parent Element).
    // Emitted AFTER `onChange` so the parent's two-way bound model is
    // up to date by the time its `(selectionChange)` handler runs.
    this.selectionChange.emit(this.value);
    this.closePanel();
    this.cdr.markForCheck();
  }

  /**
   * Chevron handler — the ONLY way the panel opens from a mouse click
   * (focus on the input does NOT open it, per UX requirement). Mirrors
   * legacy `ng-click="ctrl.selectedForm = ''; ctrl.open($event)"` from
   * `addEdit.html` L511/L527/L547:
   *   1. Reset the field (clear `value` + `displayText`) so the parent
   *      model is in sync and any prior selection is forgotten.
   *   2. Notify the parent via `onChange('')` (cascades the legacy
   *      filter chain — clearing Form Sub also clears Parent Element,
   *      etc., per `clickIcon`'s flow at L2522-L2524).
   *   3. Re-open the panel from a clean state — `openPanel()` calls
   *      `rebuildFiltered()` which uses the now-empty `displayText`,
   *      producing the FULL option list (capped by `maxResults`). This
   *      satisfies the "arrow click resets the dropdown and shows all
   *      data" requirement.
   *   4. Focus the input so keyboard navigation works immediately.
   */
  clearAndOpen(ev: MouseEvent): void {
    ev.preventDefault();
    ev.stopPropagation();
    if (this.disabled || this.loading) return;
    this.value = '';
    this.displayText = '';
    this.onChange('');
    this.filtered = [];
    this.panelOpen = false;
    this.cdr.markForCheck();
    setTimeout(() => {
      const el = this.inputRef?.nativeElement;
      el?.focus();
      this.openPanel();
      this.cdr.markForCheck();
    }, 0);
  }

  onArrowDown(ev: Event): void {
    if (!this.panelOpen || !this.filtered.length) return;
    ev.preventDefault();
    this.activeIndex = Math.min(this.activeIndex + 1, this.filtered.length - 1);
    this.cdr.markForCheck();
  }

  onArrowUp(ev: Event): void {
    if (!this.panelOpen || !this.filtered.length) return;
    ev.preventDefault();
    this.activeIndex = Math.max(this.activeIndex - 1, 0);
    this.cdr.markForCheck();
  }

  onEnter(ev: Event): void {
    if (!this.panelOpen || this.activeIndex < 0) return;
    ev.preventDefault();
    const item = this.filtered[this.activeIndex];
    if (item) this.selectItem(item);
  }

  onEscape(ev: Event): void {
    ev.preventDefault();
    this.closePanel();
    this.syncDisplayToValue();
    this.cdr.markForCheck();
  }

  @HostListener('document:click', ['$event'])
  onDocClick(ev: Event): void {
    const t = ev.target as Node;
    const host = this.hostRef.nativeElement;
    if (host.contains(t)) return;
    if (this.panelOpen) {
      this.closePanel();
      // Same parity as `onBlur` — typed text stays committed; clicking
      // outside should not revert to the last selected option.
      this.cdr.markForCheck();
    }
  }

  @HostListener('window:resize')
  onWinResize(): void {
    if (this.panelOpen) this.positionPanel();
  }

  private openPanel(): void {
    this.rebuildFiltered();
    this.panelOpen = true;
    this.activeIndex = this.filtered.length > 0 ? 0 : -1;
    this.positionPanel();
    this.attachScrollListeners();
  }

  private rebuildFiltered(): void {
    const q = (this.displayText || '').toLowerCase().trim();
    const list = Array.isArray(this.options) ? this.options : [];
    let rows = !q
      ? list.slice()
      : list.filter((row) => this.getItemLabel(row).toLowerCase().includes(q));
    if (this.maxResults > 0 && rows.length > this.maxResults) {
      rows = rows.slice(0, this.maxResults);
    }
    this.filtered = rows;
  }

  private syncDisplayToValue(): void {
    this.displayText = this.value || '';
  }

  closePanel(): void {
    this.panelOpen = false;
    this.detachScrollListeners();
    this.cdr.markForCheck();
  }

  private positionPanel(): void {
    if (!this.appendToBody) {
      this.panelTop = this.panelLeft = this.panelWidth = 0;
      return;
    }
    const el = this.inputRef?.nativeElement;
    if (!el) return;
    const r = el.getBoundingClientRect();
    this.panelTop = r.bottom + 2;
    this.panelLeft = r.left;
    this.panelWidth = r.width;
  }

  private attachScrollListeners(): void {
    this.detachScrollListeners();
    if (!this.appendToBody) return;
    let p: HTMLElement | null = this.hostRef.nativeElement.parentElement;
    while (p && p !== document.body) {
      if (p.scrollHeight > p.clientHeight) {
        p.addEventListener('scroll', this.boundReposition, true);
        this.scrollParents.push(p);
      }
      p = p.parentElement;
    }
    window.addEventListener('scroll', this.boundReposition, true);
  }

  private detachScrollListeners(): void {
    for (const el of this.scrollParents) {
      el.removeEventListener('scroll', this.boundReposition, true);
    }
    this.scrollParents = [];
    window.removeEventListener('scroll', this.boundReposition, true);
  }

  private boundReposition = (): void => {
    if (this.panelOpen) {
      this.positionPanel();
      this.cdr.markForCheck();
    }
  };
}
